import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import config from '../config';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Dialog,
} from '@mui/material';

// Larger type for every field on this form (caregivers are often older
// adults). Labels use the same size as the input text because MUI sizes the
// gap in the outline from the input's font; the shrunk label is moved up to
// stay centred on the border at this size.
const FORM_TEXT_SX = {
  '& .MuiInputBase-root': { fontSize: '1.35rem' },
  '& .MuiInputLabel-root': { fontSize: '1.35rem' },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': { transform: 'translate(14px, -12px) scale(0.75)' },
  '& .MuiFormHelperText-root': { fontSize: '1.05rem', lineHeight: 1.5, mt: 0.75 },
  '& .MuiFormControlLabel-label': { fontSize: '1.35rem' },
  '& .MuiRadio-root .MuiSvgIcon-root': { fontSize: 30 },
};

const MENU_PROPS = {
  PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '1.3rem', py: 1.25 } } },
};

const defaultForm = {
  caregiverName: '',
  patientName: '',
  needCareFor: '',
  caregiverDob: '',
  patientDob: '',
  race: '',
  educationYears: '',
  familiarity: '',
  caregiverBackground: '',
  patientBackground: '',
};

export default function CaregiverForm({ open, onClose, asPage = false }) {
  const [form, setForm] = useState(defaultForm);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isVisible = asPage || open;
  const formRef = useRef(defaultForm);
  const lastSavedPayloadRef = useRef(JSON.stringify(defaultForm));
  const saveInFlightRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const saveProfileRef = useRef(async () => {});

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const saveProfile = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      return;
    }

    const payload = formRef.current;
    const serializedPayload = JSON.stringify(payload);
    if (serializedPayload === lastSavedPayloadRef.current) {
      return;
    }

    if (saveInFlightRef.current) {
      saveQueuedRef.current = true;
      return;
    }

    saveInFlightRef.current = true;
    setIsSaving(true);
    setSaveMessage('Saving...');

    try {
      await axios.post(`${config.API_BASE_URL}/update-profile`, { ...payload, userId });
      lastSavedPayloadRef.current = serializedPayload;
      setSaveMessage('Saved.');
    } catch (err) {
      console.error('Failed to update user details:', err);
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);

      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        setTimeout(() => {
          void saveProfileRef.current();
        }, 0);
      }
    }
  }, []);

  useEffect(() => {
    saveProfileRef.current = saveProfile;
  }, [saveProfile]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const userId = localStorage.getItem('userId');
    axios
      .get(`${config.API_BASE_URL}/get-profile`, { params: { userId } })
      .then((res) => {
        if (res.data && Object.keys(res.data).length) {
          const nextForm = { ...defaultForm, ...res.data };
          setForm(nextForm);
          lastSavedPayloadRef.current = JSON.stringify(nextForm);
        }
        setSaveMessage('');
      })
      .catch((err) => {
        console.error('Failed to fetch user details:', err);
      });
  }, [isVisible]);

  const handleChange = (field) => (e) => {
    const next = { ...form, [field]: e.target.value };
    setForm(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveProfile();
  };

  const handleFieldBlur = () => {
    void saveProfileRef.current();
  };

  const handleFieldEnter = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) {
      return;
    }

    if (e.target instanceof HTMLTextAreaElement) {
      return;
    }

    e.preventDefault();
    void saveProfileRef.current();
  };

  const formContent = (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: asPage ? 0 : 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        ...FORM_TEXT_SX,
        background: asPage
          ? 'transparent'
          : 'linear-gradient(180deg, rgba(236, 247, 255, 0.62), rgba(255, 255, 255, 0.96))',
      }}
    >
      <Typography variant="h6" sx={{ fontSize: '2.2rem', fontWeight: 800, mb: 0.5, color: asPage ? '#0f2b3a' : '#e9f5fc' }}>
        Caregiver & Patient Profile
      </Typography>

      <TextField
        label="Caregiver Name"
        value={form.caregiverName}
        onChange={handleChange('caregiverName')}
        onBlur={handleFieldBlur}
        onKeyDown={handleFieldEnter}
        fullWidth
      />

      <TextField
        label="Patient Name"
        value={form.patientName}
        onChange={handleChange('patientName')}
        onBlur={handleFieldBlur}
        onKeyDown={handleFieldEnter}
        fullWidth
      />

      <TextField
        label="Need Care For"
        value={form.needCareFor}
        onChange={handleChange('needCareFor')}
        onBlur={handleFieldBlur}
        onKeyDown={handleFieldEnter}
        helperText="e.g. mobility assistance, medication reminders"
        fullWidth
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField
          label="Caregiver Date of Birth"
          type="date"
          value={form.caregiverDob}
          onChange={handleChange('caregiverDob')}
          onBlur={handleFieldBlur}
          onKeyDown={handleFieldEnter}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Patient Date of Birth"
          type="date"
          value={form.patientDob}
          onChange={handleChange('patientDob')}
          onBlur={handleFieldBlur}
          onKeyDown={handleFieldEnter}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <FormControl fullWidth>
        <InputLabel>Race</InputLabel>
        <Select
          label="Race"
          MenuProps={MENU_PROPS}
          value={form.race}
          onChange={handleChange('race')}
          onBlur={handleFieldBlur}
          onClose={handleFieldBlur}
        >
          <MenuItem value="Asian">Asian</MenuItem>
          <MenuItem value="White">White</MenuItem>
          <MenuItem value="Black">Black</MenuItem>
          <MenuItem value="Hispanic">Hispanic</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Years of Education"
        type="number"
        inputProps={{ min: 0 }}
        value={form.educationYears}
        onChange={handleChange('educationYears')}
        onBlur={handleFieldBlur}
        onKeyDown={handleFieldEnter}
        helperText="How many years of formal schooling?"
        fullWidth
      />

      <FormControl component="fieldset">
        <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, mb: 0.5, color: asPage ? '#19445b' : '#e9f5fc' }}>
          How well do you know the patient?
        </Typography>
        <RadioGroup
          value={form.familiarity}
          onChange={handleChange('familiarity')}
          onBlur={handleFieldBlur}
        >
          <FormControlLabel value="Not at all" control={<Radio />} label="Not at all" />
          <FormControlLabel value="Somewhat well" control={<Radio />} label="Somewhat well" />
          <FormControlLabel value="Well" control={<Radio />} label="Well" />
          <FormControlLabel value="Very well" control={<Radio />} label="Very well" />
        </RadioGroup>
      </FormControl>

      <TextField
        label="Caregiver Background"
        value={form.caregiverBackground}
        onChange={handleChange('caregiverBackground')}
        onBlur={handleFieldBlur}
        helperText="Brief background information about the caregiver"
        multiline
        rows={3}
        fullWidth
      />

      <TextField
        label="Patient Background"
        value={form.patientBackground}
        onChange={handleChange('patientBackground')}
        onBlur={handleFieldBlur}
        helperText="Brief background information about the patient"
        multiline
        rows={3}
        fullWidth
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <span className={`text-[1.2rem] font-semibold ${asPage ? 'text-[#2a4f63]' : 'text-[#d9eef9]'}`}>
          {isSaving ? 'Saving...' : saveMessage}
        </span>
        <span className={`text-[1.1rem] ${asPage ? 'text-[#40687d]' : 'text-[#c6dfed]'}`}>
          Auto-saves on Enter or when you leave a field.
        </span>
      </Box>
    </Box>
  );

  if (asPage) {
    return <div className="h-full overflow-auto">{formContent}</div>;
  }

  return (
    <Dialog
      open={isVisible}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      {formContent}
    </Dialog>
  );
}
